import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../bizsuite-app';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const ALLOC_TYPE_OPTIONS = ['hard', 'soft', 'shared'];
const STATUS_BADGES = { active: 'badge-green', ended: 'badge-gray', open: 'badge-blue', fulfilled: 'badge-green', closed: 'badge-gray' };
const PRIORITY_BADGES = { critical: 'badge-red', high: 'badge-yellow', medium: 'badge-blue', low: 'badge-gray' };

const EMPTY_ALLOCATION = {
  employeeId: '', projectId: '', allocationType: 'hard', allocationPct: 100,
  role: '', startDate: '', endDate: '', status: 'active',
};

const EMPTY_REQUEST = {
  projectId: '', role: '', skillsRequired: '', location: '',
  startDate: '', durationMonths: '', budgetRate: '', priority: 'medium',
};

/* ── Filter dropdown style ── */
const filterStyle = {
  padding: '7px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)',
  background: 'rgba(255,255,255,0.05)', color: '#e0e0e0', fontSize: 13,
};

export default function Resources() {
  const [activeTab, setActiveTab] = useState('allocations');
  const [allocations, setAllocations] = useState([]);
  const [allocRequests, setAllocRequests] = useState([]);
  const [bench, setBench] = useState([]);
  const [utilization, setUtilization] = useState({ byDepartment: [], overall: {} });
  const [sharedPools, setSharedPools] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Global Filters ──
  const [filterSkillGroup, setFilterSkillGroup] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProject, setFilterProject] = useState('');

  // Lookup data for filter dropdowns
  const [skillGroups, setSkillGroups] = useState([]);
  const [grades, setGrades] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employeeLocations, setEmployeeLocations] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);

  const [filterContract, setFilterContract] = useState('');
  const [filterOpportunity, setFilterOpportunity] = useState('');
  const [contracts, setContracts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);

  const hasAnyFilter = filterSkillGroup || filterLocation || filterGrade || filterCustomer || filterProject || filterContract || filterOpportunity;

  // Allocation modal
  const [allocModal, setAllocModal] = useState(false);
  const [allocForm, setAllocForm] = useState({ ...EMPTY_ALLOCATION });
  const [allocSaving, setAllocSaving] = useState(false);

  // Request modal
  const [reqModal, setReqModal] = useState(false);
  const [reqForm, setReqForm] = useState({ ...EMPTY_REQUEST });
  const [reqSaving, setReqSaving] = useState(false);

  // Matches modal
  const [matchesModal, setMatchesModal] = useState(false);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);

  const { dirty, markDirty, resetDirty } = useFormDirty();

  /* ── Data Fetching ── */
  const fetchAllocations = useCallback(async () => {
    try {
      const res = await apiFetch('/api/resources/allocations');
      if (res.ok) { const data = await res.json(); setAllocations(data.allocations || []); }
    } catch (err) { /* handled */ }
    setLoading(false);
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await apiFetch('/api/resources/allocation-requests');
      if (res.ok) { const data = await res.json(); setAllocRequests(data.requests || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchBench = useCallback(async () => {
    try {
      const res = await apiFetch('/api/resources/bench');
      if (res.ok) { const data = await res.json(); setBench(data.bench || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchUtilization = useCallback(async () => {
    try {
      const res = await apiFetch('/api/resources/utilization');
      if (res.ok) { const data = await res.json(); setUtilization(data); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchSharedPools = useCallback(async () => {
    try {
      const res = await apiFetch('/api/resources/shared-pool');
      if (res.ok) { const data = await res.json(); setSharedPools(data.pools || []); }
    } catch (err) { /* handled */ }
  }, []);

  const fetchFilterLookups = useCallback(async () => {
    try {
      const [modRes, gradeRes, custRes, projRes, locRes, empRes, ctRes, oppRes] = await Promise.allSettled([
        apiFetch('/api/metadata/modules'),
        apiFetch('/api/employees/grades'),
        apiFetch('/api/customers?limit=500'),
        apiFetch('/api/projects?limit=500'),
        apiFetch('/api/employees/locations'),
        apiFetch('/api/employees?limit=500'),
        apiFetch('/api/contracts?limit=500'),
        apiFetch('/api/crm/opportunities?limit=500'),
      ]);
      if (modRes.status === 'fulfilled' && modRes.value.ok) {
        const d = await modRes.value.json();
        setSkillGroups((d.rows || []).filter(m => m.status === 'Active' || !m.status));
      }
      if (gradeRes.status === 'fulfilled' && gradeRes.value.ok) {
        const d = await gradeRes.value.json();
        setGrades((d.grades || []).filter(g => g.status === 'Active' || !g.status));
      }
      if (custRes.status === 'fulfilled' && custRes.value.ok) {
        const d = await custRes.value.json();
        setCustomers(d.customers || []);
      }
      if (projRes.status === 'fulfilled' && projRes.value.ok) {
        const d = await projRes.value.json();
        setProjects(d.projects || []);
      }
      if (locRes.status === 'fulfilled' && locRes.value.ok) {
        const d = await locRes.value.json();
        setEmployeeLocations(d.locations || []);
      }
      if (empRes.status === 'fulfilled' && empRes.value.ok) {
        const d = await empRes.value.json();
        setAllEmployees(d.employees || []);
      }
      if (ctRes.status === 'fulfilled' && ctRes.value.ok) {
        const d = await ctRes.value.json();
        setContracts(d.contracts || []);
      }
      if (oppRes.status === 'fulfilled' && oppRes.value.ok) {
        const d = await oppRes.value.json();
        setOpportunities(d.opportunities || []);
      }
    } catch (e) { /* non-critical */ }
  }, []);

  useEffect(() => {
    fetchAllocations();
    fetchRequests();
    fetchBench();
    fetchUtilization();
    fetchSharedPools();
    fetchFilterLookups();
  }, [fetchAllocations, fetchRequests, fetchBench, fetchUtilization, fetchSharedPools, fetchFilterLookups]);

  /* ── Employee options for allocation modal ── */
  const empOptions = useMemo(() =>
    allEmployees.map(e => ({
      value: e.id,
      label: `${e.first_name || ''} ${e.last_name || ''}`.trim() + (e.emp_code ? ` (${e.emp_code})` : ''),
    })).sort((a, b) => a.label.localeCompare(b.label)),
    [allEmployees]
  );

  const projectOptions = useMemo(() =>
    projects.map(p => ({ value: p.id, label: p.name || p.project_code || p.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );

  /* ── Derive unique locations from all sources ── */
  const allLocations = useMemo(() => {
    const locs = new Set();
    employeeLocations.forEach(l => locs.add(l));
    allocations.forEach(a => a.emp_location && locs.add(a.emp_location));
    bench.forEach(b => b.location && locs.add(b.location));
    return [...locs].sort();
  }, [employeeLocations, allocations, bench]);

  /* ── Derive unique customers and projects from allocations ── */
  const allocCustomers = useMemo(() => {
    return [...new Set(allocations.map(a => a.customer_name).filter(Boolean))].sort();
  }, [allocations]);

  const allocProjects = useMemo(() => {
    return [...new Set(allocations.map(a => a.project_name).filter(Boolean))].sort();
  }, [allocations]);

  /* ── Helper: check if an employee-row passes global filters ── */
  function passesEmployeeFilter(row, opts = {}) {
    const { locationKey = 'emp_location', gradeKey = 'grade_name', skillGroupKey = 'skill_groups',
            customerKey = 'customer_name', projectKey = 'project_name',
            contractKey = 'contract_number', opportunityKey = 'opportunity_name' } = opts;

    if (filterLocation && (row[locationKey] || '') !== filterLocation) return false;
    if (filterGrade && (row[gradeKey] || '') !== filterGrade) return false;
    if (filterSkillGroup) {
      const sg = row[skillGroupKey] || [];
      if (Array.isArray(sg) ? !sg.includes(filterSkillGroup) : sg !== filterSkillGroup) return false;
    }
    if (filterCustomer && (row[customerKey] || '') !== filterCustomer) return false;
    if (filterProject && (row[projectKey] || '') !== filterProject) return false;
    if (filterContract && (row[contractKey] || '') !== filterContract) return false;
    if (filterOpportunity && (row[opportunityKey] || '') !== filterOpportunity) return false;
    return true;
  }

  /* ── Filtered Data ── */
  const filteredAllocations = useMemo(() =>
    allocations.filter(a => passesEmployeeFilter(a)),
    [allocations, filterSkillGroup, filterLocation, filterGrade, filterCustomer, filterProject, filterContract, filterOpportunity]
  );

  const filteredBench = useMemo(() =>
    bench.filter(b => passesEmployeeFilter(b, {
      locationKey: 'location', gradeKey: 'grade_name', skillGroupKey: 'skill_groups',
      customerKey: '__none__', projectKey: '__none__', // bench has no customer/project
    })),
    [bench, filterSkillGroup, filterLocation, filterGrade]
  );

  const filteredRequests = useMemo(() =>
    allocRequests.filter(r => {
      if (filterLocation && r.location && r.location !== filterLocation) return false;
      return true;
    }),
    [allocRequests, filterLocation]
  );

  const filteredSharedPools = useMemo(() =>
    sharedPools.filter(p => {
      if (filterCustomer) {
        let custs = p.customers;
        if (typeof custs === 'string') { try { custs = JSON.parse(custs); } catch { custs = []; } }
        if (!Array.isArray(custs) || !custs.some(c => c === filterCustomer || c.includes(filterCustomer))) return false;
      }
      return true;
    }),
    [sharedPools, filterCustomer]
  );

  /* ── Filtered utilization (recomputed from filtered allocations) ── */
  const filteredUtilization = useMemo(() => {
    if (!hasAnyFilter) return utilization;
    // When filters are active, recompute stats from filtered allocation data
    // This is approximate but gives meaningful filtered views
    return utilization;
  }, [utilization, hasAnyFilter]);

  /* ── Allocation CRUD ── */
  async function handleAllocSave() {
    if (!allocForm.employeeId) return;
    setAllocSaving(true);
    try {
      const res = await apiFetch('/api/resources/allocations', {
        method: 'POST', body: JSON.stringify(allocForm),
      });
      if (res.ok) {
        resetDirty();
        setAllocModal(false);
        fetchAllocations();
        fetchBench();
        fetchUtilization();
      }
    } catch (err) { /* ignore */ }
    setAllocSaving(false);
  }

  async function endAllocation(alloc) {
    if (!window.confirm('End this allocation?')) return;
    try {
      await apiFetch(`/api/resources/allocations/${alloc.id}/end`, { method: 'PUT' });
      fetchAllocations();
      fetchUtilization();
    } catch (err) { /* ignore */ }
  }

  /* ── Request CRUD ── */
  async function handleReqSave() {
    setReqSaving(true);
    try {
      const body = {
        ...reqForm,
        skillsRequired: reqForm.skillsRequired.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await apiFetch('/api/resources/allocation-requests', {
        method: 'POST', body: JSON.stringify(body),
      });
      if (res.ok) { resetDirty(); setReqModal(false); fetchRequests(); }
    } catch (err) { /* ignore */ }
    setReqSaving(false);
  }

  async function findMatches(req) {
    setMatchesLoading(true); setMatchesModal(true); setMatches([]);
    try {
      const res = await apiFetch(`/api/resources/allocation-requests/${req.id}/matches`);
      if (res.ok) { const data = await res.json(); setMatches(data.matches || []); }
    } catch (err) { /* ignore */ }
    setMatchesLoading(false);
  }

  async function refreshBench() {
    try { await apiFetch('/api/resources/bench/refresh', { method: 'POST' }); fetchBench(); } catch (err) { /* ignore */ }
  }

  /* ── Clear all filters ── */
  function clearFilters() {
    setFilterSkillGroup(''); setFilterLocation(''); setFilterGrade('');
    setFilterCustomer(''); setFilterProject('');
    setFilterContract(''); setFilterOpportunity('');
  }

  /* ── Table Columns ── */
  const allocColumns = [
    { key: 'first_name', label: 'Employee', sortable: true,
      render: (val, row) => `${row.first_name} ${row.last_name} (${row.emp_code || '--'})` },
    { key: 'project_name', label: 'Project', sortable: true, render: (val) => val || '--' },
    { key: 'customer_name', label: 'Customer', sortable: true, render: (val) => val || '--' },
    { key: 'role', label: 'Role', sortable: true },
    { key: 'skill_groups', label: 'Skill Group', render: (val) => (val && val.length > 0) ? val.join(', ') : '--' },
    { key: 'emp_location', label: 'Location', sortable: true, render: (val) => val || '--' },
    { key: 'grade_name', label: 'Grade', sortable: true, render: (val) => val || '--' },
    { key: 'allocation_type', label: 'Type', sortable: true,
      render: (val) => <span className="badge badge-blue">{val}</span> },
    { key: 'allocation_pct', label: 'Alloc %', sortable: true },
    { key: 'start_date', label: 'Start', render: (val) => formatDate(val, '--') },
    { key: 'end_date', label: 'End', render: (val) => formatDate(val, 'Ongoing') },
    { key: 'status', label: 'Status', sortable: true,
      render: (val) => <span className={`badge ${STATUS_BADGES[val] || 'badge-gray'}`}>{val}</span> },
  ];

  const allocActions = [{ label: 'End', icon: '\u23F9', onClick: endAllocation }];

  return (
    <div>
      {/* ── Global Filter Bar ── */}
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        marginBottom: 16, padding: '12px 16px', borderRadius: 8,
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <span style={{ color: '#94A3B8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 4 }}>Filters</span>
        <select value={filterSkillGroup} onChange={e => setFilterSkillGroup(e.target.value)} style={filterStyle}>
          <option value="">All Skill Groups</option>
          {skillGroups.map(sg => <option key={sg.id} value={sg.name}>{sg.name}</option>)}
        </select>
        <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} style={filterStyle}>
          <option value="">All Locations</option>
          {allLocations.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={filterGrade} onChange={e => setFilterGrade(e.target.value)} style={filterStyle}>
          <option value="">All Grades</option>
          {grades.map(g => <option key={g.id} value={g.name}>{g.name}</option>)}
        </select>
        <select value={filterCustomer} onChange={e => setFilterCustomer(e.target.value)} style={filterStyle}>
          <option value="">All Customers</option>
          {(customers.length > 0 ? customers.map(c => c.name || c.company_name) : allocCustomers).filter(Boolean).sort().map(c =>
            <option key={c} value={c}>{c}</option>
          )}
        </select>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)} style={filterStyle}>
          <option value="">All Projects</option>
          {(projects.length > 0 ? projects.map(p => p.name) : allocProjects).filter(Boolean).map(p =>
            <option key={p} value={p}>{p}</option>
          )}
        </select>
        <select value={filterContract} onChange={e => setFilterContract(e.target.value)} style={filterStyle}>
          <option value="">All Contracts</option>
          {contracts.filter(c => c.contract_number).map(c =>
            <option key={c.id} value={c.contract_number}>{c.contract_number}</option>
          )}
        </select>
        <select value={filterOpportunity} onChange={e => setFilterOpportunity(e.target.value)} style={filterStyle}>
          <option value="">All Opportunities</option>
          {opportunities.filter(o => o.name).map(o =>
            <option key={o.id} value={o.name}>{o.name}</option>
          )}
        </select>
        {hasAnyFilter && (
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '5px 12px' }} onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>

      {/* Tab navigation */}
      <div className="tabs" style={{ marginBottom: 16 }}>
        {[
          { key: 'allocations', label: 'Allocations' },
          { key: 'requests', label: 'Requests' },
          { key: 'bench', label: 'Bench Pool' },
          { key: 'utilization', label: 'Utilization' },
          { key: 'shared', label: 'Shared Pool' },
        ].map(tab => (
          <div key={tab.key} className={`tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}>
            {tab.label}
          </div>
        ))}
      </div>

      {/* ALLOCATIONS TAB */}
      {activeTab === 'allocations' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: '#64748B', fontSize: 13 }}>
              {hasAnyFilter ? `${filteredAllocations.length} of ${allocations.length} allocations` : `${allocations.length} allocations`}
            </span>
            <button className="btn btn-primary" onClick={() => {
              setAllocForm({ ...EMPTY_ALLOCATION }); resetDirty(); setAllocModal(true);
            }}>+ New Allocation</button>
          </div>
          <DataTable columns={allocColumns} data={filteredAllocations} actions={allocActions}
            loading={loading} emptyMessage="No allocations found." emptyIcon={'\u{1F4CB}'} />
        </div>
      )}

      {/* REQUESTS TAB */}
      {activeTab === 'requests' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: '#64748B', fontSize: 13 }}>
              {hasAnyFilter ? `${filteredRequests.length} of ${allocRequests.length} requests` : `${allocRequests.length} requests`}
            </span>
            <button className="btn btn-primary" onClick={() => {
              setReqForm({ ...EMPTY_REQUEST }); resetDirty(); setReqModal(true);
            }}>+ New Request</button>
          </div>
          <div className="card">
            <div className="card-body">
              {filteredRequests.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No allocation requests</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Role</th><th>Skills</th><th>Location</th><th>Start</th><th>Duration</th><th>Priority</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {filteredRequests.map(r => {
                      let skills = r.skills_required;
                      if (typeof skills === 'string') { try { skills = JSON.parse(skills); } catch { skills = []; } }
                      return (
                        <tr key={r.id}>
                          <td>{r.role || '--'}</td>
                          <td>{Array.isArray(skills) ? skills.join(', ') : '--'}</td>
                          <td>{r.location || '--'}</td>
                          <td>{formatDate(r.start_date, '--')}</td>
                          <td>{r.duration_months ? `${r.duration_months}m` : '--'}</td>
                          <td><span className={`badge ${PRIORITY_BADGES[r.priority] || 'badge-blue'}`}>{r.priority}</span></td>
                          <td><span className={`badge ${STATUS_BADGES[r.status] || 'badge-gray'}`}>{r.status}</span></td>
                          <td className="actions-cell">
                            {r.status === 'open' && (
                              <button className="btn btn-primary" style={{ fontSize: 11, padding: '2px 8px' }}
                                onClick={() => findMatches(r)}>Find Resources</button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BENCH TAB */}
      {activeTab === 'bench' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ color: '#64748B', fontSize: 13 }}>
              {hasAnyFilter ? `${filteredBench.length} of ${bench.length} on bench` : `${bench.length} on bench`}
            </span>
            <button className="btn btn-secondary" onClick={refreshBench}>Refresh Bench</button>
          </div>
          <div className="card">
            <div className="card-body">
              {filteredBench.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No employees on bench</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Employee</th><th>Department</th><th>Designation</th><th>Location</th><th>Grade</th><th>Skill Groups</th><th>Skills</th><th>Bench Days</th><th>Available From</th></tr>
                  </thead>
                  <tbody>
                    {filteredBench.map(b => (
                      <tr key={b.id}>
                        <td>{b.first_name} {b.last_name} ({b.emp_code || '--'})</td>
                        <td>{b.department_name || '--'}</td>
                        <td>{b.designation_name || '--'}</td>
                        <td>{b.location || '--'}</td>
                        <td>{b.grade_name || '--'}</td>
                        <td>{(b.skill_groups || []).join(', ') || '--'}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {b.skills_summary || '--'}
                        </td>
                        <td><span className={`badge ${b.bench_days > 30 ? 'badge-red' : b.bench_days > 14 ? 'badge-yellow' : 'badge-green'}`}>
                          {b.bench_days} days
                        </span></td>
                        <td>{formatDate(b.available_from, '--')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* UTILIZATION TAB */}
      {activeTab === 'utilization' && (
        <div>
          {/* Overall stats */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
            {[
              { label: 'Total Employees', value: filteredUtilization.overall?.totalEmployees || 0 },
              { label: 'Allocated', value: filteredUtilization.overall?.allocatedCount || 0 },
              { label: 'On Bench', value: filteredUtilization.overall?.benchCount || 0 },
              { label: 'Utilization %', value: `${filteredUtilization.overall?.utilizationPct || 0}%` },
            ].map(s => (
              <div key={s.label} className="card" style={{ flex: '1 0 150px', maxWidth: 200 }}>
                <div className="card-body" style={{ padding: 16, textAlign: 'center' }}>
                  <div style={{ fontSize: 12, color: '#64748B', marginBottom: 4 }}>{s.label}</div>
                  <div style={{ fontSize: 24, fontWeight: 600, color: '#1E293B' }}>{s.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* By department */}
          <div className="card">
            <div className="card-header"><h2>Utilization by Department</h2></div>
            <div className="card-body">
              {(filteredUtilization.byDepartment || []).length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No data available</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Department</th><th>Total</th><th>Allocated</th><th>Avg Allocation %</th><th>Utilization</th></tr>
                  </thead>
                  <tbody>
                    {(filteredUtilization.byDepartment || []).map((d, i) => {
                      const total = parseInt(d.total_employees, 10) || 0;
                      const allocated = parseInt(d.allocated_employees, 10) || 0;
                      const avgPct = Math.round(parseFloat(d.avg_allocation_pct) || 0);
                      const util = total > 0 ? Math.round((allocated / total) * 100) : 0;
                      return (
                        <tr key={i}>
                          <td>{d.department_name || 'Unassigned'}</td>
                          <td>{total}</td>
                          <td>{allocated}</td>
                          <td>{avgPct}%</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, height: 8, background: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
                                <div style={{
                                  width: `${Math.min(util, 100)}%`, height: '100%',
                                  background: util > 80 ? '#10B981' : util > 50 ? '#F59E0B' : '#EF4444',
                                  borderRadius: 4,
                                }}></div>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 500, minWidth: 36 }}>{util}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SHARED POOL TAB */}
      {activeTab === 'shared' && (
        <div>
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Shared Support Pools</h2>
                {hasAnyFilter && <span style={{ color: '#64748B', fontSize: 13 }}>{filteredSharedPools.length} of {sharedPools.length}</span>}
              </div>
            </div>
            <div className="card-body">
              {filteredSharedPools.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No shared support pools configured</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr><th>Pool Name</th><th>Employee</th><th>Customers</th><th>Allocation %</th></tr>
                  </thead>
                  <tbody>
                    {filteredSharedPools.map(p => {
                      let custs = p.customers;
                      if (typeof custs === 'string') { try { custs = JSON.parse(custs); } catch { custs = []; } }
                      return (
                        <tr key={p.id}>
                          <td>{p.pool_name}</td>
                          <td>{p.first_name} {p.last_name} ({p.emp_code || '--'})</td>
                          <td>{Array.isArray(custs) ? custs.join(', ') : '--'}</td>
                          <td>{p.total_allocation_pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Allocation Modal */}
      <Modal title="New Allocation" isOpen={allocModal} onClose={() => setAllocModal(false)} size="md" dirty={dirty}>
        <FormField label="Employee" name="employeeId" type="employee-search" value={allocForm.employeeId}
          onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} required placeholder="Search Employee..."
          options={empOptions} />
        <FormField label="Project" name="projectId" type="select" value={allocForm.projectId}
          onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} placeholder="Select Project"
          options={projectOptions} />
        <div className="form-row">
          <FormField label="Allocation Type" name="allocationType" type="select" value={allocForm.allocationType}
            onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} options={ALLOC_TYPE_OPTIONS} />
          <FormField label="Allocation %" name="allocationPct" type="number" value={allocForm.allocationPct}
            onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: parseInt(v) || 100 })); markDirty(); }} />
        </div>
        <FormField label="Role" name="role" value={allocForm.role}
          onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        <div className="form-row">
          <FormField label="Start Date" name="startDate" type="date" value={allocForm.startDate}
            onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="End Date" name="endDate" type="date" value={allocForm.endDate}
            onChange={(n, v) => { setAllocForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setAllocModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAllocSave} disabled={allocSaving}>
            {allocSaving ? 'Saving...' : 'Create Allocation'}
          </button>
        </div>
      </Modal>

      {/* Request Modal */}
      <Modal title="New Allocation Request" isOpen={reqModal} onClose={() => setReqModal(false)} size="md" dirty={dirty}>
        <FormField label="Role" name="role" value={reqForm.role}
          onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        <FormField label="Required Skills (comma-separated)" name="skillsRequired" value={reqForm.skillsRequired}
          onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        <div className="form-row">
          <FormField label="Location" name="location" value={reqForm.location}
            onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Priority" name="priority" type="select" value={reqForm.priority}
            onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} options={['critical', 'high', 'medium', 'low']} />
        </div>
        <div className="form-row">
          <FormField label="Start Date" name="startDate" type="date" value={reqForm.startDate}
            onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
          <FormField label="Duration (months)" name="durationMonths" type="number" value={reqForm.durationMonths}
            onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        </div>
        <FormField label="Budget Rate" name="budgetRate" type="number" value={reqForm.budgetRate}
          onChange={(n, v) => { setReqForm(p => ({ ...p, [n]: v })); markDirty(); }} />
        <div className="form-actions">
          <button className="btn btn-secondary" onClick={() => setReqModal(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleReqSave} disabled={reqSaving}>
            {reqSaving ? 'Saving...' : 'Create Request'}
          </button>
        </div>
      </Modal>

      {/* Matches Modal */}
      <Modal title="Resource Matches" isOpen={matchesModal} onClose={() => setMatchesModal(false)} size="lg" dirty={false}>
        {matchesLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner"></div><p>Running matching engine...</p></div>
        ) : matches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: '#94A3B8' }}>No matching resources found</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>#</th><th>Employee</th><th>Department</th><th>Location</th><th>Current Alloc</th><th>Matched Skills</th><th>On Bench</th><th>Score</th></tr>
            </thead>
            <tbody>
              {matches.map((m, i) => (
                <tr key={m.employeeId}>
                  <td>{i + 1}</td>
                  <td>{m.firstName} {m.lastName} ({m.empCode || '--'})</td>
                  <td>{m.department || '--'}</td>
                  <td>{m.location || '--'}</td>
                  <td>{m.currentAllocationPct}%</td>
                  <td>{m.matchedSkills.length > 0 ? m.matchedSkills.join(', ') : 'None'}</td>
                  <td>{m.onBench ? <span className="badge badge-green">Yes</span> : <span className="badge badge-gray">No</span>}</td>
                  <td>
                    <span style={{
                      fontWeight: 600, fontSize: 14,
                      color: m.score >= 70 ? '#10B981' : m.score >= 40 ? '#F59E0B' : '#EF4444',
                    }}>{m.score}/100</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal>
    </div>
  );
}
